import { Box, Group, Modal, SimpleGrid, Stack, useMantineTheme } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconMail } from '@tabler/icons-react';
import ActionIcon from 'components/ActionIcon';
import Button from 'components/Button';
import H3Medium from 'components/typography/H3Medium';
import P3Medium from 'components/typography/P3Medium';
import P3Regular from 'components/typography/P3Regular';
import P4Regular from 'components/typography/P4Regular';
import panic from 'errors/panic';
import { useEffect, useMemo, useState } from 'react';

const BRATISLAVA_TIME_ZONE = 'Europe/Bratislava';
const WEEKDAYS = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne'];

export interface HrNotificationCalendarPreviewProps {
  period: string;
  weekday: number;
  monthDay: number;
  windowType: string;
  windowValue: number;
  departmentIds: number[];
  termTypeIds: number[];
  loadHrTermDueDates?: HrTermDueDateLoader;
  loadHrEmailPreview?: HrNotificationEmailPreviewLoader;
}

export interface HrTermDueDateQuery {
  from: string;
  to: string;
  departmentIds: number[];
  termTypeIds: number[];
}

export type HrTermDueDateLoader = (query: HrTermDueDateQuery) => Promise<string[]>;

export interface HrNotificationEmailPreview {
  evaluationDate: string;
  itemCount: number;
  html: string;
}

export type HrNotificationEmailPreviewLoader = (evaluationDate: string) => Promise<HrNotificationEmailPreview>;

export interface HrNotificationRange {
  from: string;
  to: string;
}

/** Formats a UTC-backed calendar date as YYYY-MM-DD. */
export function toDateKey(date: Date): string {
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

/** Parses a date-only key without applying the browser timezone. */
export function fromDateKey(value: string): Date {
  return new Date(value + 'T00:00:00.000Z');
}

/** Adds calendar days to a date-only key. */
export function addCalendarDays(value: string, amount: number): string {
  const date = fromDateKey(value);
  date.setUTCDate(date.getUTCDate() + amount);
  return toDateKey(date);
}

/** Returns the current date in Europe/Bratislava. */
export function getBratislavaToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: BRATISLAVA_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return values.year + '-' + values.month + '-' + values.day;
}

/** Returns the ISO weekday where Monday is 1 and Sunday is 7. */
export function getIsoWeekday(value: string): number {
  const weekday = fromDateKey(value).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

/** Returns the final date of the month containing the supplied date. */
export function getMonthEnd(value: string): string {
  const date = fromDateKey(value);
  return toDateKey(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)));
}

/** Calculates the upcoming monitored range for one evaluation date. */
export function calculateHrNotificationRange(
  evaluationDate: string,
  windowType: string,
  windowValue: number
): HrNotificationRange {
  if (windowType === 'today') {
    return { from: evaluationDate, to: evaluationDate };
  }
  if (windowType === 'current-week') {
    return { from: evaluationDate, to: addCalendarDays(evaluationDate, 7 - getIsoWeekday(evaluationDate)) };
  }
  if (windowType === 'current-month') {
    return { from: evaluationDate, to: getMonthEnd(evaluationDate) };
  }
  if (windowType === 'next-week') {
    const from = addCalendarDays(evaluationDate, 8 - getIsoWeekday(evaluationDate));
    return { from, to: addCalendarDays(from, 6) };
  }
  if (windowType === 'next-month') {
    const date = fromDateKey(evaluationDate);
    const from = toDateKey(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1)));
    return { from, to: getMonthEnd(from) };
  }
  return { from: evaluationDate, to: addCalendarDays(evaluationDate, Math.max(1, windowValue) - 1) };
}

/** Builds a Monday-through-Sunday calendar grid including adjacent-month dates. */
export function getCalendarGrid(month: string): string[] {
  const monthDate = fromDateKey(month);
  const first = toDateKey(new Date(Date.UTC(monthDate.getUTCFullYear(), monthDate.getUTCMonth(), 1)));
  const last = getMonthEnd(first);
  const from = addCalendarDays(first, 1 - getIsoWeekday(first));
  const to = addCalendarDays(last, 7 - getIsoWeekday(last));
  const result: string[] = [];
  for (let date = from; date <= to; date = addCalendarDays(date, 1)) {
    result.push(date);
  }
  return result;
}

/** Reports whether the configured cadence evaluates on a given date. */
export function isEvaluationDate(value: string, period: string, weekday: number, monthDay: number): boolean {
  if (period === 'daily') {
    return true;
  }
  if (period === 'weekly') {
    return getIsoWeekday(value) === weekday;
  }
  const date = fromDateKey(value);
  const monthEndDay = Number(getMonthEnd(value).slice(-2));
  const effectiveDay = monthDay === 0 ? monthEndDay : Math.min(monthDay, monthEndDay);
  return date.getUTCDate() === effectiveDay;
}

/** Moves a month key by the requested number of calendar months. */
export function moveMonth(month: string, amount: number): string {
  const date = fromDateKey(month);
  return toDateKey(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + amount, 1)));
}

/** Formats a date for explanatory Slovak copy. */
export function formatSlovakDate(value: string, includeWeekday = false): string {
  return new Intl.DateTimeFormat('sk-SK', {
    timeZone: 'UTC',
    ...(includeWeekday ? { weekday: 'long' as const } : {}),
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  }).format(fromDateKey(value));
}

/** Displays configured evaluation dates and their upcoming monitored ranges. */
export default function HrNotificationCalendarPreview({
  period,
  weekday,
  monthDay,
  windowType,
  windowValue,
  departmentIds,
  termTypeIds,
  loadHrTermDueDates,
  loadHrEmailPreview,
}: HrNotificationCalendarPreviewProps) {
  const theme = useMantineTheme();
  const today = getBratislavaToday();
  const [month, setMonth] = useState(today.slice(0, 7) + '-01');
  const calendarDates = useMemo(() => getCalendarGrid(month), [month]);
  const evaluationDates = useMemo(
    () => calendarDates.filter((date) => isEvaluationDate(date, period, weekday, monthDay)),
    [calendarDates, monthDay, period, weekday]
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dueDates, setDueDates] = useState<string[]>([]);
  const [emailPreview, setEmailPreview] = useState<HrNotificationEmailPreview | null>(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);

  useEffect(() => {
    if (!selectedDate || !evaluationDates.includes(selectedDate)) {
      setSelectedDate(evaluationDates.find((date) => date >= today) ?? evaluationDates[0] ?? null);
    }
  }, [evaluationDates, selectedDate, today]);

  useEffect(() => {
    let current = true;
    setDueDates([]);
    if (loadHrTermDueDates) {
      loadHrTermDueDates({
        from: calendarDates[0],
        to: calendarDates[calendarDates.length - 1],
        departmentIds,
        termTypeIds,
      }).then(
        (dates) => {
          if (current) setDueDates(Array.from(new Set(dates)));
        },
        () => {
          if (current) setDueDates([]);
        }
      );
    }
    return () => {
      current = false;
    };
  }, [calendarDates, departmentIds, loadHrTermDueDates, termTypeIds]);

  const selectedRange = selectedDate ? calculateHrNotificationRange(selectedDate, windowType, windowValue) : null;
  const currentMonthPrefix = month.slice(0, 7);
  const monthLabel = new Intl.DateTimeFormat('sk-SK', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(fromDateKey(month));

  /** Loads and opens the production-rendered email for the selected evaluation date. */
  async function openEmailPreview() {
    if (!selectedDate || !loadHrEmailPreview) return;
    setEmailPreviewLoading(true);
    try {
      setEmailPreview(await loadHrEmailPreview(selectedDate));
    } catch (error) {
      panic(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setEmailPreviewLoading(false);
    }
  }

  return (
    <>
      <Box component="section" aria-label="Náhľad kalendára upozornení">
        <Stack spacing={16}>
          <Stack spacing={4}>
            <H3Medium>Náhľad kontrol termínov</H3Medium>
            <P3Regular color="gray.6">
              Náhľad vysvetľuje, kedy môže prebehnúť kontrola. E-mail sa odošle iba vtedy, keď existujú oprávnené
              termíny.
            </P3Regular>
          </Stack>
          <Group position="apart" noWrap>
            <ActionIcon
              variant="subtle-gray"
              size="md"
              aria-label="Predchádzajúci mesiac"
              onClick={() => setMonth((value) => moveMonth(value, -1))}
            >
              <IconChevronLeft stroke="1.5" size={24} />
            </ActionIcon>
            <P3Medium tt="capitalize">{monthLabel}</P3Medium>
            <ActionIcon
              variant="subtle-gray"
              size="md"
              aria-label="Nasledujúci mesiac"
              onClick={() => setMonth((value) => moveMonth(value, 1))}
            >
              <IconChevronRight stroke="1.5" size={24} />
            </ActionIcon>
          </Group>
          <SimpleGrid cols={7} spacing={4}>
            {WEEKDAYS.map((day) => (
              <P4Regular key={day} ta="center" color="gray.6">
                {day}
              </P4Regular>
            ))}
            {calendarDates.map((date) => {
              const evaluation = evaluationDates.includes(date);
              const due = dueDates.includes(date);
              const inRange = selectedRange ? date >= selectedRange.from && date <= selectedRange.to : false;
              const selected = date === selectedDate;
              const inCurrentMonth = date.startsWith(currentMonthPrefix);
              return (
                <Box
                  component="button"
                  type="button"
                  key={date}
                  disabled={!evaluation}
                  aria-pressed={evaluation ? selected : undefined}
                  aria-label={
                    formatSlovakDate(date, true) +
                    (evaluation ? ', Kontrola termínov' : '') +
                    (due ? ', Termín HR' : '')
                  }
                  onClick={() => setSelectedDate(date)}
                  sx={{
                    appearance: 'none',
                    position: 'relative',
                    minWidth: 0,
                    minHeight: 52,
                    padding: 4,
                    borderRadius: 4,
                    border: selected
                      ? '3px solid ' + theme.colors.red[7]
                      : inRange
                      ? '1px solid ' + theme.colors.red[5]
                      : '1px solid ' + theme.colors.gray[3],
                    background: inRange ? theme.colors.red[0] : theme.white,
                    color: inCurrentMonth ? theme.colors.gray[8] : theme.colors.gray[5],
                    cursor: evaluation ? 'pointer' : 'default',
                    textAlign: 'center',
                  }}
                >
                  <P4Regular span>{fromDateKey(date).getUTCDate()}</P4Regular>
                  <Group position="center" spacing={3} mt={4} mih={6} noWrap>
                    {evaluation && (
                      <Box
                        component="span"
                        title="Kontrola termínov"
                        aria-hidden
                        sx={{
                          display: 'block',
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: theme.colors.red[7],
                        }}
                      />
                    )}
                    {due && (
                      <Box
                        component="span"
                        title="Termín HR"
                        aria-hidden
                        sx={{
                          display: 'block',
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: theme.colors.blue[7],
                        }}
                      />
                    )}
                  </Group>
                </Box>
              );
            })}
          </SimpleGrid>
          <Group spacing={16} noWrap aria-label="Legenda kalendára">
            <Group spacing={8} noWrap>
              <Box
                aria-hidden
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.colors.red[7],
                }}
              />
              <P4Regular>Kontrola termínov – možný e-mail</P4Regular>
            </Group>
            <Group spacing={8} noWrap>
              <Box
                aria-hidden
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.colors.blue[7],
                }}
              />
              <P4Regular>Termín HR</P4Regular>
            </Group>
          </Group>
          <Button
            type="button"
            variant="secondary"
            leftIcon={<IconMail stroke={1.5} size={20} />}
            disabled={!selectedDate || !loadHrEmailPreview}
            loading={emailPreviewLoading}
            onClick={openEmailPreview}
          >
            Zobraziť náhľad e-mailu
          </Button>
        </Stack>
      </Box>
      <Modal
        opened={emailPreview !== null}
        onClose={() => setEmailPreview(null)}
        closeButtonProps={{ 'aria-label': 'Zavrieť náhľad e-mailu' }}
        title={emailPreview ? `Náhľad e-mailu pre ${formatSlovakDate(emailPreview.evaluationDate)}` : 'Náhľad e-mailu'}
        size="xl"
      >
        {emailPreview && (
          <iframe
            title={`Náhľad HR e-mailu pre ${formatSlovakDate(emailPreview.evaluationDate)}`}
            srcDoc={emailPreview.html}
            sandbox=""
            style={{ width: '100%', height: '70vh', border: 'none', background: theme.white }}
          />
        )}
      </Modal>
    </>
  );
}
